import { filter, map, sortBy, uniq } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { MDSIconKind } from "@/mds";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageHeader } from "@/pages/agent/components/agent-page-header";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { buildTaskProjectFilterItems } from "@/pages/agent/tasks/agent-task-filter-items";
import { AgentTaskViewBar } from "@/pages/agent/tasks/agent-task-view-bar";
import {
  DEFAULT_TASK_STATUS_FILTER_VALUES,
  getTaskRowsForView,
} from "@/pages/agent/tasks/agent-tasks-page.utils";
import { AgentTasksList } from "@/pages/agent/tasks/agent-tasks-list";
import { useAgentTaskRows } from "@/pages/agent/tasks/use-agent-task-rows";
import {
  ALL_PROJECTS_FILTER,
  ALL_STATUSES_FILTER,
  useAgentTasksSearchParams,
} from "@/pages/agent/tasks/use-agent-tasks-search-params";
import {
  buildAgentStatusFilterItems,
  formatStatusLabel,
  normalizeStatusValue,
} from "@/pages/agent/utils/agent-status.utils";
import { useAppStore } from "@/store";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentTasksPage = observer(() => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const { projectFilter, statusFilter, searchQuery, viewFilter, updateTaskFilters } =
    useAgentTasksSearchParams();
  const projects = store.assistantProjects.currentAssistantModels;
  const taskRows = useAgentTaskRows({ store });
  const dataLoaded =
    store.assistantProjects.currentAssistantModelsLoaded &&
    store.assistantTasks.currentAssistantModelsLoaded &&
    store.assistantFollowUps.currentAssistantModelsLoaded &&
    store.assistantTaskNoteReferences.currentAssistantModelsLoaded;

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleTaskRows = useMemo(() => {
    const filteredTaskRows = filter(taskRows, taskRow => {
      const matchesProject =
        projectFilter === ALL_PROJECTS_FILTER || taskRow.task.projectId === projectFilter;
      const matchesStatus =
        statusFilter === ALL_STATUSES_FILTER ||
        normalizeStatusValue({ status: taskRow.task.status }) === statusFilter;
      const matchesSearch =
        !normalizedSearchQuery || taskRow.searchableText.includes(normalizedSearchQuery);

      return matchesProject && matchesStatus && matchesSearch;
    });

    return getTaskRowsForView({
      taskRows: filteredTaskRows,
      view: viewFilter,
    });
  }, [normalizedSearchQuery, projectFilter, statusFilter, taskRows, viewFilter]);

  const hasProjects = projects.length > 0;
  const projectFilterItems = buildTaskProjectFilterItems({
    projectFilter,
    projectOptions: sortBy(
      map(projects, project => ({
        id: project.id,
        title: project.title,
      })),
      projectOption => projectOption.title
    ),
  });

  const statusFilterValues = sortBy(
    uniq([
      ...DEFAULT_TASK_STATUS_FILTER_VALUES,
      ...map(taskRows, taskRow => normalizeStatusValue({ status: taskRow.task.status })),
    ]),
    status => formatStatusLabel({ status })
  );
  const statusFilterItems = buildAgentStatusFilterItems({
    allStatusesFilter: ALL_STATUSES_FILTER,
    statusFilter,
    statusFilterValues,
  });
  const queryKey = JSON.stringify({
    projectFilter,
    searchQuery: normalizedSearchQuery,
    statusFilter,
    viewFilter,
  });
  const showCreationGuidance = dataLoaded && taskRows.length === 0;

  const openProject = ({ projectId }: { projectId: string }) => {
    analytics.trackItemClick({
      itemId: projectId,
      itemKind: "project",
      source: "task_project",
    });
    store.agentOverlay.openProject({ projectId });
  };

  const openTask = ({ taskId }: { taskId: string }) => {
    analytics.trackItemClick({
      itemId: taskId,
      itemKind: "task",
      source: "task_row",
    });
    store.agentOverlay.openTask({ taskId });
  };

  const openSourceNote = ({ noteId }: { noteId: string }) => {
    analytics.trackItemClick({
      itemId: noteId,
      itemKind: "note",
      source: "task_source",
    });
    store.agentOverlay.openSourceNote({ noteId });
  };

  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
    if (!task) return;

    const currentStatus = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;
    const action =
      nextStatus === AssistantTaskStatus.Completed
        ? "completed"
        : currentStatus === AssistantTaskStatus.Completed
          ? "marked_incomplete"
          : "reopened";

    analytics.trackTaskStatusToggle({ action, taskId });
    await task.updateStatus({ status: nextStatus });
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Tasks} fillsViewport>
      <AgentPageHeader
        description="Choose a focus, then narrow the work by project, status, or name."
        title="Tasks"
      />

      {showCreationGuidance ? null : (
        <>
          <AgentTaskViewBar
            selectedView={viewFilter}
            onChange={nextView => {
              analytics.trackFilterChange({ filterKind: "view", filterValue: nextView });
              updateTaskFilters({ nextViewFilter: nextView });
            }}
          />

          <AgentPageFilterBar>
            {hasProjects ? (
              <AgentFilterSelect
                label="Project"
                selectedKey={projectFilter}
                defaultKey={ALL_PROJECTS_FILTER}
                items={projectFilterItems}
                leadingIcon={MDSIconKind.Folder}
                onSelectionChange={({ key }) => {
                  analytics.trackFilterChange({ filterKind: "project", filterValue: key });
                  updateTaskFilters({ nextProjectFilter: key });
                }}
                onClear={() => {
                  analytics.trackFilterChange({
                    filterKind: "project",
                    filterValue: ALL_PROJECTS_FILTER,
                  });
                  updateTaskFilters({ nextProjectFilter: ALL_PROJECTS_FILTER });
                }}
              />
            ) : null}

            {/* No `leadingIcon`: each status has its own icon, so the trigger shows
            the selected one rather than a generic circle. */}
            <AgentFilterSelect
              label="Status"
              selectedKey={statusFilter}
              defaultKey={ALL_STATUSES_FILTER}
              items={statusFilterItems}
              onSelectionChange={({ key }) => {
                analytics.trackFilterChange({ filterKind: "status", filterValue: key });
                updateTaskFilters({ nextStatusFilter: key });
              }}
              onClear={() => {
                analytics.trackFilterChange({
                  filterKind: "status",
                  filterValue: ALL_STATUSES_FILTER,
                });
                updateTaskFilters({ nextStatusFilter: ALL_STATUSES_FILTER });
              }}
            />

            <AgentFilterSearchField
              ariaLabel="Filter tasks by name or project"
              placeholder="Filter by name or project"
              value={searchQuery}
              onChange={value => {
                analytics.trackFilterChange({
                  filterKind: "search",
                  queryLength: value.trim().length,
                });
                updateTaskFilters({ nextSearchQuery: value });
              }}
            />
          </AgentPageFilterBar>
        </>
      )}

      <AgentTasksList
        dataLoaded={dataLoaded}
        queryKey={queryKey}
        showCreationGuidance={showCreationGuidance}
        taskRows={visibleTaskRows}
        onOpenProject={openProject}
        onOpenNote={openSourceNote}
        onOpenTask={openTask}
        onOpenActivities={({ taskId }) => {
          store.agentOverlay.openActivities({ kind: "task", taskId });
        }}
        onToggleTaskComplete={toggleTaskComplete}
      />
    </AgentPageLayout>
  );
});
